import React, {useState} from 'react';
import {useTestsState} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {useDashboardData} from '@/modules/patient-dashboard/hooks/useDashboardData';
import {
    getRecentBiomarkers,
    calculateTrendChange,
    cleanBiomarkerName,
    parseNumericValue,
} from '@/shared/utils/biomarkerUtils';
import {filterTestsByCategory} from '@/modules/patient-dashboard/utils/healthCategories';
import {useTrendCardLayout} from '@/modules/patient-dashboard/hooks/useTrendCardLayout';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useDisplayRules} from '@/shared/hooks/useDisplayRules';
import type {BiomarkerDisplayRule} from '@/shared/biomarkers/displayRuleTypes';
import type {BiomarkerStatus} from '@/shared/types/biomarker';
import type {Test} from '@/shared/types/api';
import TrendCard from './TrendCard';
import SectionHeader from '@/shared/components/SectionHeader';
import Card from '@/shared/components/Card';

export interface TrendData {
    name: string;
    value: string;
    change: string;
    changeType: 'up' | 'down';
    status?: BiomarkerStatus;
}

/**
 * The carousel's data pipeline, kept pure and exported so it can be tested
 * without mounting the component (mirrors deriveAbnormalBiomarkers).
 */
export function deriveTrends(
    tests: Test[],
    selectedCategory: string,
    gender?: 'male' | 'female',
    rules: BiomarkerDisplayRule[] = []
): TrendData[] {
    const allResults = tests.flatMap(
        (test: any) =>
            test.results?.map((result: any) => ({
                ...result,
                createdDate: test.createdDate,
            })) || []
    );

    const categoryResults = filterTestsByCategory(allResults, selectedCategory);
    const categoryTestNames = new Set(categoryResults.map((result: any) => result.test_name));

    const allRecentBiomarkers = getRecentBiomarkers(tests, 50, gender, rules);
    const recentBiomarkers = allRecentBiomarkers
        .filter((biomarker) => categoryTestNames.has(biomarker.testName))
        // Numeric results only. A trend card renders an arrow and a percent
        // change, which mean nothing for a yes/no result, so binary values
        // (Positive/Negative/Detected/Not Detected) are excluded here even
        // though isTestNotPerformed considers them performed. This subsumes
        // the old !isTestNotPerformed filter: "numeric" is a strict subset of
        // "performed". Binary results still render on the results pages and
        // Smart Testing cards — only this carousel is scoped.
        .filter((biomarker) => parseNumericValue(biomarker.result) !== null)
        .slice(0, 12);

    return recentBiomarkers.map((biomarker) => {
        const trendChange = calculateTrendChange(tests, biomarker.testName);

        return {
            name: cleanBiomarkerName(biomarker.testName),
            value: biomarker.result,
            change: trendChange.changeText,
            changeType: trendChange.changeType,
            status: biomarker.status,
        };
    });
}

interface LabTrendsProps {
    selectedCategory: string;
    onTrendSelect?: (biomarkerName: string) => void;
}

// Mobile stacks cards vertically; it is display:none at md+, so its width is
// never measured and the layout stays null there.
const MOBILE_ITEMS_PER_VIEW = 4;

const LabTrends: React.FC<LabTrendsProps> = ({selectedCategory, onTrendSelect}) => {
    const testsState = useTestsState();
    const [currentIndex, setCurrentIndex] = useState(0);
    const {user} = useAuth();
    const {rules} = useDisplayRules();

    // Get gender from user info for gender-specific biomarker configs (matches LabResults.tsx)
    const gender: 'male' | 'female' | undefined =
        user?.userInfo?.gender === 'F' ? 'female' : user?.userInfo?.gender === 'M' ? 'male' : undefined;

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: tests} = useDashboardData();
    const isLoading = testsState.loading;

    const trends = deriveTrends(tests, selectedCategory, gender, rules);

    const {viewportRef, probe, layout} = useTrendCardLayout(trends);
    const itemsPerView = layout?.cardsPerView ?? MOBILE_ITEMS_PER_VIEW;
    const maxIndex = Math.max(0, trends.length - itemsPerView);

    const scrollLeft = () => {
        setCurrentIndex((prev) => Math.max(0, prev - 1));
    };

    const scrollRight = () => {
        setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
    };

    const scrollUp = () => {
        setCurrentIndex((prev) => Math.max(0, prev - 1));
    };

    const scrollDown = () => {
        setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
    };

    // Reset index when trends change
    React.useEffect(() => {
        setCurrentIndex(0);
    }, [trends.length, selectedCategory]);

    // Widening the window fits more cards, which shrinks maxIndex. Without this
    // a user scrolled to the end stays scrolled past the content and sees blank
    // space where cards should be.
    React.useEffect(() => {
        setCurrentIndex((prev) => Math.min(prev, maxIndex));
    }, [maxIndex]);

    return (
        <section>
            <div className="flex justify-between">
                <SectionHeader title="Your Lab Trends" />
                <p className="inline-flex mb-2 text-sm text-mld-muted-text items-end">
                    A snapshot into recent changes in your latest lab results
                </p>
            </div>

            <Card variant="elevated" rounded="2xl" className="p-6">
                <div className="relative">
                    {isLoading ? (
                        <div className="text-center py-8 text-mld-card-text">Loading lab trends...</div>
                    ) : trends.length === 0 ? (
                        <div className="text-center py-8 text-mld-card-text">No lab trends available</div>
                    ) : (
                        <>
                            {/* Mobile: Up/Down navigation */}
                            <div className="md:hidden">
                                {trends.length > itemsPerView && (
                                    <>
                                        <button
                                            onClick={scrollUp}
                                            disabled={currentIndex === 0}
                                            className="absolute -top-1 left-1/2 -translate-x-1/2 z-10 w-8 h-8 bg-white border border-mld-panel-border rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-50 shadow-sm">
                                            <svg
                                                className="w-4 h-4 text-mld-card-text"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 15l7-7 7 7"
                                                />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={scrollDown}
                                            disabled={currentIndex >= maxIndex}
                                            className="absolute -bottom-1 left-1/2 -translate-x-1/2 z-10 w-8 h-8 bg-white border border-mld-panel-border rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-50 shadow-sm">
                                            <svg
                                                className="w-4 h-4 text-mld-card-text"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </button>
                                    </>
                                )}
                            </div>

                            {/* Tablet/Desktop: Left/Right navigation */}
                            <div className="hidden md:block">
                                {trends.length > itemsPerView && (
                                    <>
                                        <button
                                            onClick={scrollLeft}
                                            disabled={currentIndex === 0}
                                            className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white border border-mld-panel-border rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-50 shadow-sm">
                                            <svg
                                                className="w-4 h-4 text-mld-card-text"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 19l-7-7 7-7"
                                                />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={scrollRight}
                                            disabled={currentIndex >= maxIndex}
                                            className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white border border-mld-panel-border rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-50 shadow-sm">
                                            <svg
                                                className="w-4 h-4 text-mld-card-text"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 5l7 7-7 7"
                                                />
                                            </svg>
                                        </button>
                                    </>
                                )}
                            </div>

                            {/* Mobile: Vertical stack */}
                            <div className="md:hidden">
                                <div className="flex flex-col gap-3 py-4">
                                    {trends.slice(currentIndex, currentIndex + itemsPerView).map((trend, index) => (
                                        <div key={currentIndex + index} className="w-full">
                                            <TrendCard trend={trend} onClick={() => onTrendSelect?.(trend.name)} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Tablet/Desktop: one strip, geometry measured at runtime.
                                Cards are sized so exactly cardsPerView of them fill the
                                viewport, which puts card N+1 fully outside this
                                overflow-hidden edge instead of slicing it.
                                mx-8 leaves room for the -left-2/-right-2 nav buttons. */}
                            <div className="hidden md:block mx-8 overflow-hidden" ref={viewportRef}>
                                <div
                                    className={`flex gap-3 transition-transform duration-300 ease-out${layout ? '' : ' invisible'}`}
                                    style={{transform: `translateX(-${currentIndex * (layout?.slotWidth ?? 0)}px)`}}>
                                    {trends.map((trend, index) => (
                                        <TrendCard
                                            key={index}
                                            trend={trend}
                                            width={layout?.cardWidth}
                                            onClick={() => onTrendSelect?.(trend.name)}
                                        />
                                    ))}
                                </div>
                            </div>
                            {probe}
                        </>
                    )}
                </div>
            </Card>
        </section>
    );
};

export default LabTrends;
